import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  TextField,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Checkbox,
  Badge,
  Box,
  Divider,
  Banner,
  Spinner,
} from '@shopify/polaris';
import { useApi } from '../providers/ApiProvider';
import { t } from '../i18n';

export interface MarketCountry {
  code: string;
  name: string;
}

interface LocationPickerModalProps {
  open: boolean;
  selectedCodes: string[];
  onClose: () => void;
  onConfirm: (selection: MarketCountry[]) => void;
}

interface MarketsApiResponse {
  source: 'shopify_markets' | 'shopify_ships_to' | 'demo_fallback';
  countries: MarketCountry[];
  message?: string;
}

/**
 * Location multi-select limited to the shop’s Shopify Markets countries.
 */
export function LocationPickerModal({
  open,
  selectedCodes,
  onClose,
  onConfirm,
}: LocationPickerModalProps) {
  const api = useApi();
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<string[]>(selectedCodes);
  const [countries, setCountries] = useState<MarketCountry[]>([]);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraft(selectedCodes);
    setQuery('');
    setLoading(true);
    setError(null);
    api
      .get<MarketsApiResponse>('/stores/markets/countries')
      .then((res) => {
        setCountries(res.countries || []);
        setInfo(res.message || null);
        // Drop selections that are no longer in Markets
        const allowed = new Set((res.countries || []).map((c) => c.code));
        setDraft((prev) => prev.filter((c) => allowed.has(c)));
      })
      .catch((e: Error) => {
        setError(e.message || 'Could not load Markets countries');
        setCountries([]);
      })
      .finally(() => setLoading(false));
  }, [open, selectedCodes, api]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [query, countries]);

  function toggle(code: string) {
    setDraft((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  function selectAllVisible() {
    const codes = filtered.map((c) => c.code);
    setDraft((prev) => Array.from(new Set([...prev, ...codes])));
  }

  function clearAll() {
    setDraft([]);
  }

  const nameOf = (code: string) =>
    countries.find((c) => c.code === code)?.name || code;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Select Markets locations")}
      primaryAction={{
        content:
          draft.length === 0
            ? 'Clear all locations'
            : `Save ${draft.length} location${draft.length === 1 ? '' : 's'}`,
        onAction: () => {
          const selected = draft.map((code) => ({
            code,
            name: countries.find((c) => c.code === code)?.name || code,
          }));
          onConfirm(selected);
          onClose();
        },
        disabled: loading,
      }}
      secondaryActions={[{ content: t("Cancel"), onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">

          {loading && (
            <InlineStack gap="200" blockAlign="center">
              <Spinner size="small" />
              <Text as="span" tone="subdued">
                {t("Loading Markets…")}
              </Text>
            </InlineStack>
          )}

          {error && (
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          )}

          {info && !loading && (
            <Banner tone="info">
              <p>{info}</p>
            </Banner>
          )}

          {!loading && !error && (
            <>
              <TextField
                label={t("Search countries")}
                labelHidden
                value={query}
                onChange={setQuery}
                placeholder={t("Search by country name or code…")}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => setQuery('')}
              />
              <InlineStack align="space-between" blockAlign="center">
                <Text as="span" variant="bodySm" tone="subdued">
                  {draft.length} {t("selected ·")} {countries.length} {t("in Markets")}
                </Text>
                <InlineStack gap="200">
                  <Button size="slim" onClick={selectAllVisible} disabled={!filtered.length}>
                    {t("Select visible")}
                  </Button>
                  <Button size="slim" onClick={clearAll}>
                    {t("Clear")}
                  </Button>
                </InlineStack>
              </InlineStack>
              <Divider />
              <div
                style={{
                  maxHeight: 320,
                  overflowY: 'auto',
                  border: '1px solid #e1e3e5',
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                <BlockStack gap="100">
                  {filtered.map((c) => (
                    <Box key={c.code} padding="100">
                      <Checkbox
                        label={`${c.name} (${c.code})`}
                        checked={draft.includes(c.code)}
                        onChange={() => toggle(c.code)}
                      />
                    </Box>
                  ))}
                  {filtered.length === 0 && (
                    <Text as="p" tone="subdued">
                      {countries.length === 0
                        ? 'No Markets countries available for this shop.'
                        : 'No countries match your search.'}
                    </Text>
                  )}
                </BlockStack>
              </div>
              {draft.length > 0 && (
                <InlineStack gap="100" wrap>
                  {draft.slice(0, 12).map((code) => (
                    <Badge key={code}>{nameOf(code)}</Badge>
                  ))}
                  {draft.length > 12 && (
                    <Badge>{`+${draft.length - 12} more`}</Badge>
                  )}
                </InlineStack>
              )}
            </>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
